/**
 * Phase 107 (D-3) — evidence attachment-type contract.
 *
 * Maps the template-authoring `evidence_type` values (authored in
 * ProcedureChecklist.tsx EVIDENCE_TYPES) to a file-picker `accept=` MIME set
 * plus a human label for the "Expected: {label}" hint. Unknown / null / 'None'
 * degrade to attachment_type 'any' ({ accept: undefined, label: undefined }) per
 * 107-UI-SPEC §2.
 *
 * IMPORTANT (threat T-107-01): the `accept=` filter and the hint are UX ONLY —
 * NOT a security control. A client can bypass `accept=`. Server-side validation
 * at /api/v1/utl/files (via fileService.upload) remains authoritative.
 */

export interface EvidenceAcceptSpec {
  /** File-picker accept= MIME set; undefined => unconstrained ('any' degrade). */
  accept?: string;
  /** Human label for the "Expected: {label}" hint; undefined => hint hidden. */
  label?: string;
}

/** The 'any' degrade default used for null / 'None' / unknown evidence_type. */
export const EVIDENCE_ANY: EvidenceAcceptSpec = { accept: undefined, label: undefined };

/**
 * Authoring-value -> accept/label map. Keys mirror the EVIDENCE_TYPES list in
 * ProcedureChecklist.tsx: ['None','Document','Screenshot','Excel',
 * 'Text Explanation','External Link'].
 */
export const EVIDENCE_TYPE_ACCEPT: Record<string, EvidenceAcceptSpec> = {
  None: EVIDENCE_ANY,
  Document: {
    accept: '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    label: 'PDF document',
  },
  Screenshot: {
    accept: 'image/*',
    label: 'Image',
  },
  Excel: {
    accept: '.xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv',
    label: 'Spreadsheet',
  },
  // Text Explanation / External Link are not file uploads — they degrade to 'any'
  // (a free attachment is still permitted, no type constraint).
  'Text Explanation': EVIDENCE_ANY,
  'External Link': EVIDENCE_ANY,
};

/**
 * Resolve an evidence_type to its accept/label spec, degrading null / 'None' /
 * unknown values to the 'any' default.
 */
export function resolveEvidenceAccept(evidenceType?: string | null): EvidenceAcceptSpec {
  if (!evidenceType) return EVIDENCE_ANY;
  return EVIDENCE_TYPE_ACCEPT[evidenceType] ?? EVIDENCE_ANY;
}

/**
 * True when a chosen file's MIME/extension is acceptable for the given
 * evidence_type. The 'any' degrade (no accept set) always returns true. This
 * mirrors the browser accept= behavior for inline pre-upload rejection copy —
 * NOT a security gate.
 */
export function fileMatchesEvidenceType(
  file: { name: string; type: string },
  evidenceType?: string | null,
): boolean {
  const { accept } = resolveEvidenceAccept(evidenceType);
  if (!accept) return true;
  const tokens = accept.split(',').map((t) => t.trim().toLowerCase());
  const fileType = (file.type || '').toLowerCase();
  const fileName = (file.name || '').toLowerCase();
  return tokens.some((token) => {
    if (!token) return false;
    if (token.startsWith('.')) return fileName.endsWith(token);
    if (token.endsWith('/*')) return fileType.startsWith(token.slice(0, -1));
    return fileType === token;
  });
}
